import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Puzzle, MessageSquarePlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { usePlugins } from './usePlugins'
import { BackButton, SectionHeading } from '@tendbot/ui'
import { defaultStagger } from '@tendbot/ui'
import { PluginCard, FeatureSelectionModal } from './components'
import { SkeletonCard } from '@tendbot/ui'
import { usePluginConnections } from './usePluginConnections'

interface PluginsProps {
  onBack: () => void
}

export default function Plugins({ onBack }: PluginsProps): React.JSX.Element {
  const { t } = useTranslation('plugins')
  const { catalog, installed, loading, togglePlugin, installPlugin, uninstallPlugin, refresh, fetchIfNeeded } = usePlugins()
  const [expandedPlugin, setExpandedPlugin] = useState<string | null>(null)

  const {
    connectingPlugin,
    featureSelectPlugin,
    setFeatureSelectPlugin,
    getConnectionsForPlugin,
    handleConnect,
    handleFeatureConnect,
    handleConnectGmail,
    handleConnectCalendar,
    handleConnectContacts,
    handleDisconnect,
    handleSetDefault,
    handleToggleCalendarSync,
    handleToggleServiceSync,
    handleRunThrough,
  } = usePluginConnections({ catalog, installed, installPlugin, refresh })

  // Trigger lazy plugin data fetch when this screen mounts
  useEffect(() => {
    fetchIfNeeded()
  }, [fetchIfNeeded])

  // Split catalog into categories based on install status (tendbot-email managed in Settings)
  const filteredCatalog = catalog.filter(p => p.id !== 'tendbot-email')
  const installedIds = new Set(installed.map(p => p.id))
  const installedCatalog = filteredCatalog.filter(p => installedIds.has(p.id))
  const available = filteredCatalog.filter(p => !installedIds.has(p.id))

  const { container: containerVariants, item: itemVariants } = defaultStagger

  if (loading) {
    return (
      <div className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4">
        <div className="max-w-2xl mx-auto">
          <BackButton onClick={onBack} />
          <div className="space-y-3 mt-6">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4"
    >
      <div className="max-w-2xl mx-auto">
      {/* Back row */}
      <BackButton onClick={onBack} />

      {/* Header */}
      <motion.div variants={itemVariants} className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
            <Puzzle size={20} className="text-gold" />
          </div>
          <div>
            <h2 className="font-display text-lg text-ivory font-semibold">{t('title')}</h2>
            <p className="text-pearl/60 text-xs">{t('subtitle')}</p>
          </div>
        </div>
        <p className="text-pearl/50 text-sm leading-relaxed">
          {t('description')}
        </p>
      </motion.div>

      {/* Installed Plugins */}
      {installedCatalog.length > 0 && (
        <motion.div variants={itemVariants} className="mb-6">
          <SectionHeading>
            {t('sections.installed')}
          </SectionHeading>
          <div className="space-y-2">
            {installedCatalog.map(plugin => {
              const inst = installed.find(p => p.id === plugin.id)
              const { primary, all, defaultConnectionId } = plugin.authRequired
                ? getConnectionsForPlugin(plugin.id)
                : { primary: null, all: [], defaultConnectionId: null }
              return (
                <PluginCard
                  key={plugin.id}
                  plugin={plugin}
                  enabled={inst?.enabled ?? false}
                  settingDefs={inst?.settingDefs}
                  userSettings={inst?.settings}
                  onToggle={() => togglePlugin(plugin.id, !(inst?.enabled ?? false))}
                  onUninstall={() => uninstallPlugin(plugin.id)}
                  expanded={expandedPlugin === plugin.id}
                  onExpand={() => setExpandedPlugin(expandedPlugin === plugin.id ? null : plugin.id)}
                  connectionDetails={primary}
                  allConnections={all}
                  defaultConnectionId={defaultConnectionId}
                  connecting={connectingPlugin === plugin.id}
                  onConnect={() => handleConnect(plugin.id)}
                  onConnectGmail={handleConnectGmail}
                  onConnectCalendar={handleConnectCalendar}
                  onConnectContacts={handleConnectContacts}
                  onDisconnect={handleDisconnect}
                  onSetDefault={(connId) => handleSetDefault(plugin.id, connId)}
                  onAddAccount={() => handleConnect(plugin.id)}
                  onToggleCalendarSync={handleToggleCalendarSync}
                  onToggleServiceSync={handleToggleServiceSync}
                  onRunThrough={primary && inst?.supportsRunThrough ? () => handleRunThrough(plugin.id) : undefined}
                  runThroughLastRunAt={inst?.runThroughLastRunAt}
                />
              )
            })}
          </div>
        </motion.div>
      )}

      {/* Available Plugins */}
      {available.length > 0 && (
        <motion.div variants={itemVariants} className="mb-6">
          <SectionHeading>
            {t('sections.available')}
          </SectionHeading>
          <div className="space-y-2">
            {available.map(plugin => (
              <PluginCard
                key={plugin.id}
                plugin={plugin}
                enabled={false}
                installable
                onInstall={() => installPlugin(plugin.id)}
                connectionDetails={plugin.authRequired ? getConnectionsForPlugin(plugin.id).primary : null}
                connecting={connectingPlugin === plugin.id}
                onConnect={() => handleConnect(plugin.id)}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Missing plugin CTA */}
      <motion.div variants={itemVariants} className="mb-6 mt-2">
        <div className="rounded-xl border border-gold/10 bg-charcoal/50 p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <MessageSquarePlus size={16} className="text-gold/60" />
            <p className="text-ivory/80 text-sm font-medium">{t('missingPlugin.title')}</p>
          </div>
          <p className="text-pearl/60 text-xs mb-3">{t('missingPlugin.description')}</p>
          <a
            href="mailto:hello@tend.bot?subject=Plugin%20suggestion"
            className="inline-flex items-center gap-1.5 text-xs text-gold hover:text-gold-light transition-colors"
          >
            {t('missingPlugin.button')}
          </a>
        </div>
      </motion.div>

      </div>

      {/* Feature selection modal */}
      <AnimatePresence>
        {featureSelectPlugin && (
          <FeatureSelectionModal
            pluginName={featureSelectPlugin.name}
            pluginIcon={featureSelectPlugin.icon}
            pluginColor={featureSelectPlugin.color}
            featureGroups={featureSelectPlugin.featureGroups!}
            onConnect={handleFeatureConnect}
            onClose={() => setFeatureSelectPlugin(null)}
            connecting={connectingPlugin === featureSelectPlugin.id}
            beta={featureSelectPlugin.id === 'google-workspace'}
          />
        )}
      </AnimatePresence>

    </motion.div>
  )
}
